import { useState } from 'react';
import { pprApi, type PprJob } from '@/lib/apiClient';
import { fmtUsdPrecise, fmtPct, fmtDate } from '../pprShared';

/** Mirror the server's filename so the saved file matches the API's. */
function reportFilename(job: PprJob): string {
  const safe = `${job.jobNumber}_${job.jobName}`
    .replace(/[^a-z0-9_\-]+/gi, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
  return `PPR_${safe || 'report'}.docx`;
}

/**
 * Report tab — printable summary view.
 *
 * Pulls together the job header, contact responses, financial roll-up, and
 * coordinator notes into a single read-only layout designed to look good on
 * letter paper. Click Print to dump it to PDF via the browser's print dialog;
 * the `@media print` rules in PprJobDetail.tsx hide the surrounding chrome
 * (back link, tab nav, top header) so the printed output is just the report.
 *
 * No new backend — every value comes from the already-loaded job.
 */
export default function ReportTab({ job }: { job: PprJob }) {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleDownload() {
    setError(null);
    setDownloading(true);
    try {
      const blob = await pprApi.downloadReport(job.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = reportFilename(job);
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(`Download failed: ${(err as Error).message}`);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <>
      <div className="ppr-report-toolbar">
        <button className="btn btn-primary btn-sm" onClick={handleDownload} disabled={downloading}>
          {downloading ? 'Generating…' : '⬇ Download Report Template (.docx)'}
        </button>
        <button className="btn btn-secondary btn-sm" onClick={() => window.print()}>
          🖨 Print
        </button>
      </div>
      {error && (
        <p style={{ color: '#8F2414', font: 'var(--ew-fw-regular) var(--ew-fs-caption)/1.4 var(--ew-font)', marginBottom: 'var(--ew-s-2)' }}>
          {error}
        </p>
      )}

      <div className="ppr-report">
        {/* ─── Header ───────────────────────────────────────────── */}
        <h2>Post Project Review</h2>
        <p className="ppr-report-meta">
          {job.jobName} · Job #{job.jobNumber}<br />
          Closed {fmtDate(job.closeDate)} · Review type: {job.reviewType} · Status: {job.status}
        </p>

        {/* ─── Job summary ──────────────────────────────────────── */}
        <h3>Summary</h3>
        <div className="ppr-report-grid">
          <ReportField label="Job Value" value={fmtUsdPrecise(job.jobValue)} />
          <ReportField label="Contract Value" value={fmtUsdPrecise(job.contractValue)} />
          <ReportField label="Original Bid" value={fmtUsdPrecise(job.originalBid)} />
          <ReportField label="Total Costs" value={fmtUsdPrecise(job.totalCosts)} />
          <ReportField label="Total Costs (w/o Directs)" value={fmtUsdPrecise(job.totalCostsWoDirects)} />
          <ReportField
            label="Financials Complete"
            value={job.financialsComplete ? fmtDate(job.financialsCompleteDate) : '—'}
          />
        </div>

        {/* ─── Margins ──────────────────────────────────────────── */}
        <h3>Margins</h3>
        <div className="ppr-report-grid">
          <ReportField label="Original Bid Margin" value={`${fmtUsdPrecise(job.originalBidMargin)} (${fmtPct(job.originalBidMarginPct)})`} />
          <ReportField label="Final Margin" value={`${fmtUsdPrecise(job.finalMargin)} (${fmtPct(job.finalMarginPct)})`} />
          <ReportField label="Super Margin" value={`${fmtUsdPrecise(job.superMargin)} (${fmtPct(job.superMarginPct)})`} />
        </div>

        {/* ─── Equipment / materials / sub / labor / per diem ─── */}
        <h3>Detail</h3>
        <div className="ppr-report-grid">
          <ReportField label="Equipment Bid" value={fmtUsdPrecise(job.equipmentBid)} />
          <ReportField label="Equipment Costs" value={fmtUsdPrecise(job.equipmentCosts)} />
          <ReportField label="Material Bid" value={fmtUsdPrecise(job.materialBid)} />
          <ReportField label="Actual Material Expense" value={fmtUsdPrecise(job.actualMaterialExpense)} />
          <ReportField label="Sub Bid" value={fmtUsdPrecise(job.subBid)} />
          <ReportField label="Sub Costs" value={fmtUsdPrecise(job.subCosts)} />
          <ReportField label="Total Bid Labor Hours" value={fmtHours(job.totalBidLaborHours)} />
          <ReportField label="Actual Job Hours" value={fmtHours(job.actualJobHours)} />
          <ReportField label="Straight-Time Hours" value={fmtHours(job.straightTimeHours)} />
          <ReportField label="OT Hours" value={fmtHours(job.otHours)} />
          <ReportField label="Per Diem" value={fmtUsdPrecise(job.perDiem)} />
        </div>

        {/* ─── Contacts ─────────────────────────────────────────── */}
        <h3>Contacts &amp; Responses</h3>
        <table className="ppr-report-contacts">
          <thead>
            <tr>
              <th>Position</th>
              <th>Name</th>
              <th>Notified</th>
              <th>Responded</th>
            </tr>
          </thead>
          <tbody>
            {(job.contacts ?? []).map((c) => (
              <tr key={c.id}>
                <td>{c.position}</td>
                <td>{c.contactName || <span style={{ color: '#999', fontStyle: 'italic' }}>not set</span>}</td>
                <td>{c.notificationSent ? fmtDate(c.notificationDate) : '—'}</td>
                <td>{c.responseReceived ? fmtDate(c.responseDate) : '—'}</td>
              </tr>
            ))}
            {(job.contacts ?? []).length === 0 && (
              <tr><td colSpan={4} style={{ textAlign: 'center', color: '#999' }}>No contacts</td></tr>
            )}
          </tbody>
        </table>

        {/* ─── Notes ────────────────────────────────────────────── */}
        {job.notes && (
          <>
            <h3>Notes</h3>
            <div className="ppr-report-narrative">{job.notes}</div>
          </>
        )}

        {/* ─── President consultation ───────────────────────────── */}
        {job.presidentConsulted && (
          <>
            <h3>President Consultation</h3>
            <div className="ppr-report-narrative">
              {job.presidentNotes || <em>Consulted — no notes recorded</em>}
            </div>
          </>
        )}

        {/* ─── Outcome flag ─────────────────────────────────────── */}
        {job.broaderMeetingRequired && (
          <>
            <h3>Outcome</h3>
            <p>⚠ Broader meeting required.</p>
          </>
        )}
      </div>
    </>
  );
}

function ReportField({ label, value }: { label: string; value: string }) {
  return (
    <div className="ppr-report-field">
      <span className="label">{label}</span>
      <span className="value">{value}</span>
    </div>
  );
}

function fmtHours(v: number | null): string {
  if (v === null || v === undefined) return '—';
  return v.toLocaleString('en-US', { maximumFractionDigits: 2 }) + ' hr';
}
